import { useDragAndDrop } from '../hooks/useDragAndDrop';

import { useHolidayStore, GiftState } from '@/store/useHolidayStore';

const ASSET_PATH = '/christmas-tree-campaign';

interface GiftConfig {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex: number;
}

interface GiftBoxProps {
  gift: GiftState;
  config: GiftConfig;
  canvasSize: { width: number; height: number };
  fireplaceConfig: { x: number; y: number; width: number; height: number };
}

export function GiftBox({ gift, config, canvasSize, fireplaceConfig }: GiftBoxProps) {
  const {
    fireplaceOn,
    startBurningGift,
    finishBurningGift,
    setDraggingGiftId,
    selectedGiftId,
    setSelectedGiftId,
    wrappedVerified,
    setShowWrappedModal,
  } = useHolidayStore();

  // Determine if this gift can be dragged (only after wrapped verification and if it's the selected gift)
  const canDrag = fireplaceOn && wrappedVerified && selectedGiftId === gift.id && !gift.burned && !gift.burning;

  const handleDrop = (_x: number, _y: number, isOverDropZone: boolean) => {
    if (isOverDropZone && fireplaceOn) {
      startBurningGift(gift.id);
      setTimeout(() => {
        finishBurningGift(gift.id);
      }, 50);
    }
    setDraggingGiftId(null);
  };

  const { dragProps, isDragging } = useDragAndDrop({
    disabled: !canDrag,
    dropZoneId: 'fireplace-zone',
    onDragStart: () => {
      setDraggingGiftId(gift.id);
    },
    onDrop: handleDrop,
  });

  // Handle click to select gift and open wrapped modal
  const handleClick = () => {
    if (!fireplaceOn || wrappedVerified || gift.burned || gift.burning) return;
    setSelectedGiftId(gift.id);
    setShowWrappedModal(true);
  };

  // Don't render if burned
  if (gift.burned) return null;

  // Hide other gifts after wrapped verification (only show the selected one)
  if (wrappedVerified && selectedGiftId !== gift.id) return null;

  // Calculate percentage positions
  const leftPercent = (config.x / canvasSize.width) * 100;
  const topPercent = (config.y / canvasSize.height) * 100;
  const widthPercent = (config.width / canvasSize.width) * 100;
  const heightPercent = (config.height / canvasSize.height) * 100;

  // Determine cursor style
  const isClickable = fireplaceOn && !wrappedVerified && !gift.burned && !gift.burning;

  return (
    <img
      {...(canDrag ? dragProps : {})}
      onClick={handleClick}
      src={`${ASSET_PATH}/${config.id}.svg`}
      alt={config.id}
      className={`
        absolute select-none
        ${isDragging ? 'scale-110 drop-shadow-2xl' : ''}
        ${gift.burning ? 'animate-gift-burn' : ''}
        ${fireplaceOn && !gift.burning && !isDragging ? 'animate-holiday-pulse' : ''}
        ${!fireplaceOn ? 'opacity-90 grayscale-[20%]' : ''}
        ${isClickable ? 'cursor-pointer hover:scale-105 transition-transform' : ''}
        ${canDrag ? 'cursor-grab active:cursor-grabbing' : ''}
      `}
      style={{
        left: `${leftPercent}%`,
        top: `${topPercent}%`,
        width: `${widthPercent}%`,
        height: `${heightPercent}%`,
        zIndex: isDragging ? 100 : gift.burning ? 100 : config.zIndex,
        ...(canDrag ? dragProps.style : {}),
        transition: gift.burning ? 'none' : (canDrag ? dragProps.style.transition : 'transform 0.2s'),
      }}
      draggable={false}
    />
  );
}
